<ng-container *ngIf="pricingModel$ | async as pricingModel">
  <div class="cf-flex-row cf-justify-content-end cf-gap-12">
    <div class="budget-factor-wrapper cf-flex-row cf-gap-24">
      <div class="checkbox-container">
        <mat-checkbox [formControl]="showBudgetPriceControl" color="primary">
          <label class="ac-form-field-label cf-flex">{{ 'UI.ProjectPrices.ShowBudgetPrice' | translate }}</label>
        </mat-checkbox>
      </div>
      <div [ngClass]="{ disabled: !showBudgetPrice() }" class="filter-field cf-flex-column">
        <label class="ac-form-field-label cf-flex">{{ 'UI.ProjectPrices.ProjectBudgetFactor' | translate }}</label>
        <mat-form-field appearance="outline" class="cf-flex-1" [subscriptSizing]="subscriptSizing || 'dynamic'">
          <input
            type="text"
            inputmode="numeric"
            matInput
            appNumberInput
            [localeCode]="localeCode$ | async"
            [numberFormControl]="projectBudgetFactorControl"
            [numberOfDecimals]="projectEntity.Properties['ProjectBudgetFactor'].MeasurementUnit?.Decimals"
            [disabled]="!showBudgetPrice()"
          />
        </mat-form-field>
        <div *ngFor="let message of budgetFactorMessages()">
          <div
            [ngClass]="{
              'ac-form-field-message-error': message.Properties['Level']?.Value === 0,
              'ac-form-field-message-warning': message.Properties['Level']?.Value === 1,
              'ac-form-field-message-info': message.Properties['Level']?.Value === 2
            }"
          >
            {{ message.Properties['Text']?.Value || (message.Properties['TextId']?.Value | translate) }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <table class="price-table">
    <thead class="price-table-sticky-header">
      <tr>
        <th class="price-table-header-cell border">{{ 'UI.ProjectPrices.HoodColumn' | translate }}</th>
        <th class="price-table-header-cell border" rowspan="2">{{ 'UI.Common.Text.IncludeInExports' | translate }}</th>
        <th class="price-table-header-cell border" colspan="5">{{ 'UI.ProjectPrices.PriceDataColumn' | translate }}</th>
        <th class="price-table-header-cell border" colspan="2">{{ 'UI.ProjectPrices.FreightColumn' | translate }}</th>
      </tr>
      <tr>
        <th class="price-table-header-cell border"></th>
        <th class="price-table-header-cell">{{ 'UI.ProjectPrices.ProductColumn' | translate }}</th>
        <th class="numeric-column price-table-header-cell">
          {{ 'UI.ProjectPrices.CostPriceColumn' | translate }}
          <span *ngIf="currencyMeasurementUnit$ | async as currencyUnit"> ({{ currencyUnit.TextId | translate }})</span>
        </th>
        <th class="price-table-header-cell">{{ 'UI.ProjectPrices.Factor' | translate }}</th>
        <th class="numeric-column price-table-header-cell">
          {{ 'UI.ProjectPrices.NetPrice' | translate }}
          <span *ngIf="currencyMeasurementUnit$ | async as currencyUnit"> ({{ currencyUnit.TextId | translate }})</span>
        </th>
        <th class="numeric-column price-table-header-cell border">
          {{ 'UI.ProjectPrices.BudgetPrice' | translate }}
          <span *ngIf="currencyMeasurementUnit$ | async as currencyUnit"> ({{ currencyUnit.TextId | translate }})</span>
        </th>
        <th class="price-table-header-cell">{{ 'UI.ProjectPrices.NumberOfModules' | translate }}</th>
        <th class="price-table-header-cell">
          {{ 'GastroFlowHood.Weight' | translate }}
          <span *ngIf="weightMeasurementUnit$ | async as weightUnit">({{ weightUnit.TextId | translate }})</span>
        </th>
      </tr>
    </thead>

    <tbody>
      <ng-container *ngFor="let hood of pricingModel.hoods; trackBy: trackHoodPrice; index as j">
        <tr
          *ngFor="let product of hood.rows; trackBy: trackHoodProduct; index as i"
          class="price-table-row"
          [ngClass]="{ even: j % 2 === 0, odd: j % 2 !== 0 }"
        >
          <td *ngIf="i === 0" class="column-name border-right" [rowSpan]="hood.rows.length">
            {{ hood.hoodName }}
            <ng-container *ngIf="hoodMessageStatuses$ | async as statuses">
              <app-message-icon *ngIf="statuses[hood.hoodId].hasErrors" [messageLevel]="0"></app-message-icon>
              <app-message-icon *ngIf="statuses[hood.hoodId].hasWarnings" [messageLevel]="1"></app-message-icon>
              <app-message-icon *ngIf="statuses[hood.hoodId].hasInfo" [messageLevel]="2"></app-message-icon>
            </ng-container>
            <div></div>
          </td>
          <td *ngIf="i === 0" class="border-right align-center" [rowSpan]="hood.rows.length">
            <mat-icon svgIcon="check" *ngIf="hood.includeInExports"></mat-icon>
          </td>

          <td>{{ product.productName | translate }}</td>
          <td class="numeric-column"><app-numeric-property-display [property]="product.price.costPrice"></app-numeric-property-display></td>
          <td>
            <app-price-table-number-input
              [property]="product.price.factor"
              [languageCode]="localeCode$ | async"
              [productId]="hood.hoodId"
              (changed)="factorChanged($event, hood.hoodId, product.productType)"
            ></app-price-table-number-input>
          </td>
          <td class="numeric-column"><app-numeric-property-display [property]="product.price.netPrice"></app-numeric-property-display></td>
          <td class="border-right numeric-column">
            <ng-container *ngIf="product.productType === 'fireExtinguishing' && product.price.budgetPrice.Rules.EnableIf">
              <app-price-table-number-input
                *ngIf="showPrice(product.price.budgetPrice)"
                [rightAlign]="true"
                [property]="product.price.budgetPrice"
                [languageCode]="localeCode$ | async"
                (changed)="priceChanged($event, hood.hoodId, product.productType)"
              >
              </app-price-table-number-input>
            </ng-container>
            <ng-container *ngIf="product.productType !== 'fireExtinguishing' || !product.price.budgetPrice.Rules.EnableIf">
              <app-numeric-property-display
                *ngIf="showPrice(product.price.budgetPrice)"
                [property]="product.price.budgetPrice"
              ></app-numeric-property-display>
            </ng-container>
          </td>
          <td>
            <app-numeric-property-display *ngIf="product.numberOfModules" [property]="product.numberOfModules">
            </app-numeric-property-display>
          </td>
          <td>
            <app-numeric-property-display *ngIf="product.weight" [property]="product.weight"></app-numeric-property-display>
          </td>
        </tr>
      </ng-container>

      <!-- Totals row -->
      <tr class="price-table-row total-row">
        <td class="column-name border-right">{{ 'UI.ProjectPrices.Total' | translate }}</td>
        <td></td>
        <td>{{ 'UI.ProjectPrices.Project' | translate }}</td>
        <td class="numeric-column">
          <app-numeric-property-display [property]="pricingModel.total.price.costPrice"></app-numeric-property-display>
        </td>
        <td></td>
        <td class="numeric-column">
          <app-numeric-property-display [property]="pricingModel.total.price.netPrice"></app-numeric-property-display>
        </td>
        <td class="numeric-column border-right">
          <app-numeric-property-display
            *ngIf="showPrice(pricingModel.total.price.budgetPrice)"
            [property]="pricingModel.total.price.budgetPrice"
          ></app-numeric-property-display>
        </td>
        <td><app-numeric-property-display [property]="pricingModel.totalNumberOfModules"></app-numeric-property-display></td>
        <td>
          <app-numeric-property-display [property]="pricingModel.totalWeight"></app-numeric-property-display>
        </td>
      </tr>

      <tr class="price-table-row total-row">
        <td class="border-right">{{ 'UI.ProjectPrices.FreightColumn' | translate }}</td>
        <td class="border-right" colspan="6"></td>
        <td class="border-right" colspan="2">
          {{ 'UI.ProjectPrices.LoadingMeters' | translate }}
        </td>
      </tr>

      <tr class="price-table-row">
        <td class="border-right"></td>
        <td class="border-right" colspan="6"></td>
        <td class="border-right" colspan="2">
          <app-numeric-property-display [property]="pricingModel.loadingMeters"></app-numeric-property-display>
        </td>
      </tr>
    </tbody>
  </table>
</ng-container>
